import { useState, useEffect, useCallback } from 'react';
import {
  X, Zap, ChevronRight, Flame, PhoneCall, Mail, MessageCircle, Send,
  FileText, Target, Trash2, AlertTriangle, Calendar
} from 'lucide-react';
import { API, STATUTS, StatutBadge, SecteurTag, ScoreBadge, PriorityDot } from './constants';

function navigateTab(tab) {
  // Internal CRM tabs (handled by CrmPage)
  const internal = ['dashboard', 'pipeline', 'rapide', 'relances', 'programmes', 'recherche', 'sources', 'config', 'ia-knowledge'];
  if (internal.includes(tab)) {
    // Triggered via prop onSetTab
    return null;
  }
  // External tabs (PatronApp level)
  sessionStorage.setItem('patron_tab', tab);
  window.location.reload();
}

export function CrmDashboard({ stats, relances, hotProspects, onDismissHot, onDeleteProspect, onGoProspecter, onSetTab }) {
  const [feedback, setFeedback] = useState({ replies_count: 0, unsubscribes_count: 0, items: [] });

  const loadFeedback = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/crm/feedback`);
      const d = await r.json();
      setFeedback(d);
    } catch {}
  }, []);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const totalActive = Object.entries(stats.pipeline || {}).reduce(
    (sum, [k, v]) => !['gagne', 'perdu'].includes(k) ? sum + v : sum, 0
  );
  const rdvCount = stats.pipeline?.rdv || 0;

  const goTo = (tab) => {
    const internal = ['dashboard', 'pipeline', 'rapide', 'relances', 'programmes', 'recherche', 'sources', 'config', 'ia-knowledge'];
    if (internal.includes(tab) && onSetTab) onSetTab(tab);
    else navigateTab(tab);
  };

  return (
    <div className="space-y-4" data-testid="crm-dashboard">

      {/* ═══ HERO TRIO — 3 action cards ═══ */}
      <div className="grid grid-cols-3 gap-2 md:gap-3">
        {/* 1. Réponses à traiter */}
        <button
          onClick={() => goTo('crm-inbox')}
          className="group relative bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 hover:border-emerald-400 rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg"
          data-testid="hero-replies">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md">
              <MessageCircle size={16} className="text-white md:hidden" />
              <MessageCircle size={20} className="text-white hidden md:block" />
            </div>
            {feedback.replies_count > 0 && (
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            )}
          </div>
          <p className="text-3xl md:text-4xl font-black text-emerald-700 leading-none">{feedback.replies_count}</p>
          <p className="text-[10px] md:text-xs font-bold text-emerald-700/80 mt-1 uppercase tracking-wider">Réponses</p>
          <p className="text-[9px] md:text-[10px] text-emerald-600/60 mt-0.5">à traiter</p>
        </button>

        {/* 2. Relances aujourd'hui */}
        <button
          onClick={() => goTo('relances')}
          className={`group relative rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg border-2 ${
            relances.length > 0
              ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300 hover:border-orange-500'
              : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:border-gray-300'
          }`}
          data-testid="hero-relances">
          <div className="flex items-center justify-between mb-2">
            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center shadow-md ${
              relances.length > 0 ? 'bg-orange-500' : 'bg-gray-400'
            }`}>
              <AlertTriangle size={16} className="text-white md:hidden" />
              <AlertTriangle size={20} className="text-white hidden md:block" />
            </div>
            {relances.length > 0 && (
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            )}
          </div>
          <p className={`text-3xl md:text-4xl font-black leading-none ${relances.length > 0 ? 'text-orange-700' : 'text-gray-400'}`}>
            {relances.length}
          </p>
          <p className={`text-[10px] md:text-xs font-bold mt-1 uppercase tracking-wider ${relances.length > 0 ? 'text-orange-700/80' : 'text-gray-500'}`}>
            Relances
          </p>
          <p className={`text-[9px] md:text-[10px] mt-0.5 ${relances.length > 0 ? 'text-orange-600/60' : 'text-gray-400'}`}>
            en retard
          </p>
        </button>

        {/* 3. RDV à venir */}
        <button
          onClick={() => goTo('pipeline')}
          className={`group relative rounded-2xl p-3 md:p-4 text-left transition-all hover:shadow-lg border-2 ${
            rdvCount > 0
              ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 hover:border-amber-500'
              : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:border-gray-300'
          }`}
          data-testid="hero-rdv">
          <div className="flex items-center justify-between mb-2">
            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center shadow-md ${
              rdvCount > 0 ? 'bg-amber-500' : 'bg-gray-400'
            }`}>
              <Calendar size={16} className="text-white md:hidden" />
              <Calendar size={20} className="text-white hidden md:block" />
            </div>
          </div>
          <p className={`text-3xl md:text-4xl font-black leading-none ${rdvCount > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
            {rdvCount}
          </p>
          <p className={`text-[10px] md:text-xs font-bold mt-1 uppercase tracking-wider ${rdvCount > 0 ? 'text-amber-700/80' : 'text-gray-500'}`}>
            RDV
          </p>
          <p className={`text-[9px] md:text-[10px] mt-0.5 ${rdvCount > 0 ? 'text-amber-600/60' : 'text-gray-400'}`}>
            programmés
          </p>
        </button>
      </div>

      {/* ═══ PRIMARY ACTION — GO PROSPECTER ═══ */}
      <button onClick={onGoProspecter}
        className="w-full bg-gradient-to-r from-[#009893] to-[#00b5ae] hover:from-[#00807b] hover:to-[#009893] text-white rounded-xl p-4 flex items-center justify-between shadow-lg shadow-[#009893]/20 transition-all group"
        data-testid="dashboard-go-btn">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Zap size={20} />
          </div>
          <div className="text-left">
            <p className="font-bold text-sm">GO PROSPECTER</p>
            <p className="text-xs text-white/70">Lancer une session ciblée</p>
          </div>
        </div>
        <ChevronRight size={20} className="text-white/50 group-hover:translate-x-1 transition-transform" />
      </button>

      {/* ═══ QUICK ACTIONS ROW ═══ */}
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => goTo('crm-campagne')}
          className="bg-white border border-gray-200 hover:border-purple-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm"
          data-testid="quick-campagne">
          <Send size={16} className="text-purple-600" />
          <span className="text-[10px] font-bold text-gray-700 text-center">Nouvelle campagne</span>
        </button>
        <button onClick={() => goTo('pipeline')}
          className="bg-white border border-gray-200 hover:border-blue-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm"
          data-testid="quick-pipeline">
          <Target size={16} className="text-blue-600" />
          <span className="text-[10px] font-bold text-gray-700 text-center">Mes Prospects ({stats.total || 0})</span>
        </button>
        <button onClick={() => goTo('email-templates')}
          className="bg-white border border-gray-200 hover:border-amber-300 rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all hover:shadow-sm"
          data-testid="quick-templates">
          <FileText size={16} className="text-amber-600" />
          <span className="text-[10px] font-bold text-gray-700 text-center">Templates Email</span>
        </button>
      </div>

      {/* ═══ HOT PROSPECTS ═══ */}
      {hotProspects.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="hot-prospects">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={14} className="text-orange-500" />
            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Prospects chauds à closer</h3>
            <span className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-bold ml-auto">
              {hotProspects.length}
            </span>
          </div>
          <div className="space-y-1">
            {hotProspects.slice(0, 5).map((p, i) => (
              <div key={p.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 group">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] font-mono text-gray-300 w-4">{i + 1}.</span>
                  <PriorityDot priorite={p.priorite} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 truncate">{p.nom}</p>
                    <div className="flex items-center gap-1.5">
                      <SecteurTag secteur={p.secteur} />
                      <StatutBadge statut={p.statut} size="xs" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ScoreBadge score={p.scoreIA} />
                  {p.telephone && (
                    <a href={`tel:${p.telephone}`} className="p-1.5 rounded hover:bg-green-50 text-gray-400 hover:text-green-600 transition-all" title={p.telephone} data-testid={`call-${p.id}`}>
                      <PhoneCall size={13} />
                    </a>
                  )}
                  {p.email && (
                    <a href={`mailto:${p.email}`} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-all" title={p.email} data-testid={`mail-${p.id}`}>
                      <Mail size={13} />
                    </a>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); onDismissHot(p.id); }}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-300 hover:text-gray-600 transition-all opacity-0 group-hover:opacity-100"
                    title="Masquer" data-testid={`dismiss-hot-${p.id}`}>
                    <X size={13} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Mettre ${p.nom} a la corbeille ?`)) onDeleteProspect(p.id); }}
                    className="p-1.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-600 transition-all opacity-0 group-hover:opacity-100"
                    title="Supprimer" data-testid={`delete-hot-${p.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ PIPELINE COMPACT ═══ */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="pipeline-summary">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Pipeline</h3>
          <span className="text-[10px] text-gray-400">{totalActive} actifs / {stats.total || 0} total</span>
        </div>
        <div className="space-y-1.5">
          {Object.entries(STATUTS).map(([key, s]) => {
            const count = stats.pipeline?.[key] || 0;
            if (count === 0) return null;
            const max = Math.max(...Object.values(stats.pipeline || {}), 1);
            return (
              <button key={key} onClick={() => goTo('pipeline')} className="w-full flex items-center gap-2 hover:opacity-80 transition-opacity">
                <span className="text-[10px] w-16 text-right text-gray-500 font-medium shrink-0">{s.label}</span>
                <div className="flex-1 bg-gray-50 rounded-full h-6 overflow-hidden">
                  <div className="h-full rounded-full flex items-center px-2 transition-all duration-700"
                    style={{ width: `${Math.max((count / max) * 100, 10)}%`, background: `linear-gradient(135deg, ${s.color}, ${s.color}cc)` }}>
                    <span className="text-[10px] text-white font-bold drop-shadow">{count}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ DERNIÈRES RÉPONSES (top 3) ═══ */}
      {feedback.items && feedback.items.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="recent-feedback">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <MessageCircle size={11} className="text-emerald-600" /> Dernières réponses
            </h3>
            <button onClick={() => goTo('crm-inbox')} className="text-[10px] text-emerald-600 font-bold hover:underline">
              Voir tout →
            </button>
          </div>
          <div className="space-y-1">
            {feedback.items.slice(0, 3).map((it, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-emerald-50/50">
                <div className={`w-2 h-2 rounded-full shrink-0 ${it.type === 'reply' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-gray-900 truncate">{it.nom}</p>
                  {it.contact && it.contact !== it.nom && (
                    <p className="text-[10px] text-gray-500 truncate">{it.contact}</p>
                  )}
                </div>
                <span className="text-[9px] text-gray-400 font-mono shrink-0">
                  {it.when ? new Date(it.when).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
